/**
 * Supabase Client — singleton loaded before all other app modules.
 * Exposes window.SupabaseClient and window.getSupabaseUser.
 *
 * Requires window.APP_CONFIG.SUPABASE_URL and SUPABASE_ANON_KEY (from config.js).
 *
 * ES module (roadmap §3). It stays FIRST among the module scripts in index.html:
 * modules execute in document order, and auth.js, storage.js and cloud-sync.js all
 * read window.SupabaseClient. The Supabase SDK itself is a classic CDN script, so
 * it has already run by the time this deferred module evaluates.
 *
 * Both exports are null when config.js is absent — the guards below return early
 * rather than throwing, so a missing config degrades to "signed out" instead of a
 * blank app.
 */
let _client = null;
let _getUser = null;

(function () {
    if (!window.supabase) {
        console.error('Supabase SDK not loaded. Add the CDN script before supabase-client.js.');
        return;
    }

    const url  = window.APP_CONFIG && window.APP_CONFIG.SUPABASE_URL;
    const key  = window.APP_CONFIG && window.APP_CONFIG.SUPABASE_ANON_KEY;

    if (!url || !key) {
        console.error('SUPABASE_URL or SUPABASE_ANON_KEY missing in config.js.');
        return;
    }

    window.SupabaseClient = window.supabase.createClient(url, key, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true   // handles OAuth + magic-link callbacks
        }
    });

    /** Returns the current authenticated user, or null. */
    _getUser = async function () {
        if (!window.SupabaseClient) return null;
        const { data: { user } } = await window.SupabaseClient.auth.getUser();
        return user;
    };

    _client = window.SupabaseClient;
})();

// ES module surface. The window.SupabaseClient assignment above remains the transition shim
// (AGENTS.md SKILL 1) — every other module still reaches it through the global.
export const SupabaseClient = _client;
export const getSupabaseUser = _getUser;
export default SupabaseClient;

